/**
 * Recording a VideoMail in the browser.
 *
 * A greeting or a voicemail message is one person talking to their own
 * camera. Asking a server-side recorder to launch a browser and join a call
 * to capture that cost about ten seconds before the first frame was written,
 * and anyone who started signing on the cue lost the opening of their take.
 * The browser already holds the camera, so it records the take itself and
 * uploads the file.
 *
 * This module holds the parts worth testing on their own: choosing a
 * container the browser can actually produce, and the rules for when a take
 * may be uploaded.
 */
/**
 * Containers in the order we prefer them.
 *
 * MP4 first because Safari produces nothing else and every player handles it.
 * WebM with VP9 next for its quality at a given size, then VP8 for older
 * Chromium, then whatever the browser considers a default WebM.
 */
export const VIDEOMAIL_MIME_PREFERENCES = [
    'video/mp4;codecs=avc1,mp4a',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm'
];
/**
 * The media types the upload route accepts. A container we cannot name to the
 * server is of no use, however well the browser records it.
 */
export const VIDEOMAIL_UPLOAD_TYPES = ['video/mp4', 'video/webm'];
/**
 * Reduces a full MIME string to the type the upload route allows.
 *
 * @param {string} mimeType - A recorder MIME string, possibly with codecs.
 * @returns {string|undefined} The bare media type, when it is one we accept.
 */
export function toUploadContentType(mimeType) {
    const bare = String(mimeType || '').split(';')[0].trim().toLowerCase();
    return VIDEOMAIL_UPLOAD_TYPES.includes(bare) ? bare : undefined;
}
/**
 * Picks the best container this browser can record.
 *
 * @param {Function} isTypeSupported - The recorder's own support test.
 * @returns {string|undefined} The chosen MIME string, or undefined when the
 * browser can record none of them.
 */
export function pickVideoMailMimeType(isTypeSupported) {
    return VIDEOMAIL_MIME_PREFERENCES.find(type => {
        try {
            return isTypeSupported(type);
        }
        catch {
            return false;
        }
    });
}
/**
 * Decides whether a finished take should be sent to the server.
 *
 * An empty recording is the common failure when a camera is revoked mid-take;
 * uploading it would replace a working greeting with a blank one. A take with
 * no usable container cannot be described to the upload route at all.
 *
 * @param {object} take - The finished take.
 * @param {number} take.size - Bytes recorded.
 * @param {string} take.mimeType - The container the recorder produced.
 * @returns {IUploadDecision} Whether to upload, and why not when refusing.
 */
export function shouldUploadTake({ size, mimeType }) {
    if (!size) {
        return { upload: false, reason: 'The recording came back empty. Record again.' };
    }
    if (!toUploadContentType(mimeType)) {
        return { upload: false, reason: 'This browser recorded a format we cannot store. Try a different browser.' };
    }
    return { upload: true };
}
